/**
 * TalentLens AI - Search Store
 * Zustand store for search state and history
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface RecentSearch {
  query: string;
  filters?: Record<string, any>;
  timestamp: number;
}

interface SavedSearch {
  id: string;
  name: string;
  query: string;
  filters?: Record<string, any>;
  createdAt: number;
}

interface SearchPreferences {
  defaultSemantic: boolean;
  defaultViewMode: 'compact' | 'standard' | 'detailed';
  defaultSortBy: 'relevance' | 'date' | 'experience';
  showFiltersOnLoad: boolean;
}

interface SearchState {
  // Recent searches
  recentSearches: RecentSearch[];
  addRecentSearch: (query: string, filters?: Record<string, any>) => void;
  clearRecentSearches: () => void;
  removeRecentSearch: (query: string) => void;

  // Saved searches
  savedSearches: SavedSearch[];
  saveSearch: (name: string, query: string, filters?: Record<string, any>) => void;
  deleteSavedSearch: (id: string) => void;

  // Preferences
  preferences: SearchPreferences;
  updatePreferences: (prefs: Partial<SearchPreferences>) => void;

  // UI State
  viewMode: 'compact' | 'standard' | 'detailed';
  setViewMode: (mode: 'compact' | 'standard' | 'detailed') => void;
  sortBy: 'relevance' | 'date' | 'experience';
  setSortBy: (sort: 'relevance' | 'date' | 'experience') => void;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;

  // Selected results for bulk actions
  selectedResults: Set<string>;
  toggleSelected: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelected: () => void;
}

const MAX_RECENT_SEARCHES = 10;

export const useSearchStore = create<SearchState>()(
  persist(
    (set, get) => ({
      // Recent searches
      recentSearches: [],

      addRecentSearch: (query: string, filters?: Record<string, any>) => {
        if (!query.trim()) return;

        const recent = get().recentSearches.filter(s => s.query !== query);
        const newSearch: RecentSearch = {
          query: query.trim(),
          filters,
          timestamp: Date.now(),
        };

        set({
          recentSearches: [newSearch, ...recent].slice(0, MAX_RECENT_SEARCHES),
        });
      },

      clearRecentSearches: () => set({ recentSearches: [] }),

      removeRecentSearch: (query: string) => {
        set({
          recentSearches: get().recentSearches.filter(s => s.query !== query),
        });
      },

      // Saved searches
      savedSearches: [],

      saveSearch: (name: string, query: string, filters?: Record<string, any>) => {
        const newSaved: SavedSearch = {
          id: crypto.randomUUID(),
          name,
          query,
          filters,
          createdAt: Date.now(),
        };

        set({
          savedSearches: [...get().savedSearches, newSaved],
        });
      },

      deleteSavedSearch: (id: string) => {
        set({
          savedSearches: get().savedSearches.filter(s => s.id !== id),
        });
      },

      // Preferences
      preferences: {
        defaultSemantic: true,
        defaultViewMode: 'standard',
        defaultSortBy: 'relevance',
        showFiltersOnLoad: false,
      },

      updatePreferences: (prefs: Partial<SearchPreferences>) => {
        set({
          preferences: { ...get().preferences, ...prefs },
        });
      },

      // UI State
      viewMode: 'standard',
      setViewMode: (mode) => set({ viewMode: mode }),

      sortBy: 'relevance',
      setSortBy: (sort) => set({ sortBy: sort }),

      filtersOpen: false,
      setFiltersOpen: (open) => set({ filtersOpen: open }),

      // Selected results
      selectedResults: new Set(),

      toggleSelected: (id: string) => {
        const selected = new Set(get().selectedResults);
        if (selected.has(id)) {
          selected.delete(id);
        } else {
          selected.add(id);
        }
        set({ selectedResults: selected });
      },

      selectAll: (ids: string[]) => {
        set({ selectedResults: new Set(ids) });
      },

      clearSelected: () => {
        set({ selectedResults: new Set() });
      },
    }),
    {
      name: 'search-storage',
      partialize: (state) => ({
        recentSearches: state.recentSearches,
        savedSearches: state.savedSearches,
        preferences: state.preferences,
      }),
    }
  )
);

// Popular skills for zero-state (could be fetched from API)
export const POPULAR_SKILLS = [
  'JavaScript',
  'Python',
  'React',
  'TypeScript',
  'Node.js',
  'AWS',
  'Java',
  'Docker',
];

// Quick filter presets
export const QUICK_FILTERS = [
  { label: '5+ Years Exp', filters: { minYearsExp: 5 } },
  { label: 'Senior Level', filters: { roleLevel: 'senior' } },
  { label: 'Recently Added', filters: { dateRange: 'week' } },
  { label: 'Processed Only', filters: { status: ['processed'] } },
];
